import PopUpBase from '@components/PopUpBase';
import { IPopUp } from '@hooks/usePopUp';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import color from '@theme/Colors';
import ChecklistRow from './ChecklistRow';
import SummaryStatCard, { SummaryStatItem } from './SummaryStatCard';

export type VerificationSummary = {
    zone: string;
    contravention: string;
    date: string;
    plate: string;
    vehicle: {
        make?: string;
        model?: string;
        color?: string;
        fuelType?: string;
    };
    permit: { hasPermit: boolean };
    duplicates: { hasDuplicate: boolean };
    geoZone: { duration: string; gracePeriodSec: number };
    overstay: { isOverstay: boolean; duration: string };
    entered: string;
    exited: string;
    isForeign: boolean;
};

type Props = IPopUp & {
    summary: VerificationSummary;
    onApprove?: () => void;
};

const NOT_FOUND = 'Not Found';

const PlateBadge = ({ plate }: { plate: string }) => (
    <Typography
        component="span"
        sx={{
            display: 'inline-block',
            bgcolor: '#FFFF00',
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
            border: `1px solid ${color.textPrimary}`,
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: 0.3,
            color: color.textPrimary,
        }}
    >
        {plate || '—'}
    </Typography>
);

const formatVehicle = (v: VerificationSummary['vehicle']) => {
    const parts = [v.make, v.model, v.color, v.fuelType].filter(Boolean);
    return parts.length ? parts.join(' · ') : NOT_FOUND;
};

export default function ConfirmApproveDialog(props: Props) {
    const { summary } = props;

    const vehicleLabel = formatVehicle(summary.vehicle);
    const permitLabel = summary.permit.hasPermit ? 'Has permit' : 'No permit';
    const duplicatesLabel =
        summary.duplicates.hasDuplicate
            ? `Has duplicates`
            : 'No duplicates';
    const overstayLabel = summary.overstay.isOverstay ? summary.overstay.duration : 'No Overstay';

    const stats: SummaryStatItem[] = [
        { label: 'Entry', value: summary.entered || '—' },
        { label: 'Exited', value: summary.exited || '—' },
        { label: 'Duration', value: summary.geoZone.duration || '—' },
        {
            label: 'Grace period',
            value: `${summary.geoZone.gracePeriodSec}s`,
            tone: 'success',
        },
        {
            label: 'Overstay',
            value: overstayLabel,
            tone: summary.overstay.isOverstay ? 'error' : 'success',
        },
        { label: 'Foreign plate', value: summary.isForeign ? 'Yes' : 'No' },
    ];

    return (
        <PopUpBase
            open={props.open}
            onClose={props.onClose}
            onConfirm={() => {}}
            dialogProps={{
                fullWidth: true,
                onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.stopPropagation();
                        props.onClose?.();
                        props.onApprove?.();
                    } else if (e.key === 'Escape') {
                        e.stopPropagation();
                        props.onClose?.();
                    }
                },
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: 780,
                    },
                },
            }}
            title={<Typography variant="h4">Are you sure you want to approve this contravention?</Typography>}
            minWidthButton={120}
            desc={
                <Box sx={{ width: '100%' }}>
                    <Stack bgcolor={color.grey100} borderRadius={1} p={1}>
                        <SummaryStatCard
                            variant="separated"
                            columns={3}
                            items={[
                                { label: 'Zone', value: summary.zone || '—' },
                                {
                                    label: 'Contravention type',
                                    value: summary.contravention || '—',
                                },
                                { label: 'Contravention date', value: summary.date || '—' },
                            ]}
                        />
                    </Stack>

                    <Box sx={{ mt: 1.5 }} />

                    <ChecklistRow
                        icon={<DocumentScannerOutlinedIcon />}
                        title="License plate"
                        value={<PlateBadge plate={summary.plate} />}
                    />
                    <ChecklistRow
                        icon={<DirectionsCarOutlinedIcon />}
                        title="Vehicle info"
                        value={vehicleLabel}
                        tone={vehicleLabel !== NOT_FOUND ? 'default' : 'error'}
                    />
                    <ChecklistRow
                        icon={<BadgeOutlinedIcon />}
                        title="Permit check"
                        value={permitLabel}
                        tone={summary.permit.hasPermit ? 'error' : 'success'}
                    />
                    <ChecklistRow
                        icon={<ContentCopyOutlinedIcon />}
                        title="Duplicate check"
                        value={duplicatesLabel}
                        tone={summary.duplicates.hasDuplicate ? 'error' : 'success'}
                        divider={false}
                    />

                    <Box sx={{ mt: 1.5 }}>
                        <SummaryStatCard items={stats} columns={3} />
                    </Box>
                </Box>
            }
            customActions={
                <Stack direction="row" justifyContent="space-between" width="100%">
                    <Button variant="cancel" sx={{ minWidth: 120 }} onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ minWidth: 120 }}
                        onClick={() => {
                            props.onClose?.();
                            props.onApprove?.();
                        }}
                    >
                        Approve (Enter)
                    </Button>
                </Stack>
            }
        />
    );
}
